import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Play, Loader2, Mic } from 'lucide-react';
import { useMeditationCategories, useUnfinishedMeditations } from '@/hooks/useMeditations';
import { useContextualRecommendation } from '@/hooks/useContextualRecommendation';
import { useTelegram } from '@/hooks/useTelegram';
import { BreathingTab } from '@/components/BreathingTab';

export function RelaxTab() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { categories, loading: catLoading } = useMeditationCategories();
  const { items: unfinished } = useUnfinishedMeditations();
  const { recommendation, loading: recLoading } = useContextualRecommendation();

  function openPlayer(slug: string) {
    haptic('light');
    nav(`/relax/player/${slug}`);
  }
  function openCategory(slug: string) {
    haptic('light');
    nav(`/relax/category/${slug}`);
  }
  if (catLoading) {
    return (
      <div className="flex-1 flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-emerald-glow" />
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto py-4 space-y-5">
      {/* 🟣 Контекстная рекомендация */}
      {recommendation && !recLoading && (
        <div className="px-5">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-2xl p-4 border border-emerald-glow/30 bg-gradient-to-br from-emerald-glow/10 to-sky-glow/5"
          >
            <div className="text-[10px] text-emerald-glow font-bold uppercase tracking-wider mb-2">
              {recommendation.badgeText}
            </div>
            
            <button
              onClick={() => openPlayer(recommendation.meditation.slug)}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-bg/40 active:scale-[0.98] transition-all"
            >
              <div className="text-3xl shrink-0">
                {recommendation.meditation.emoji || '🎵'}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="font-display font-bold text-sm">
                  {recommendation.meditation.title}
                </div>
                <div className="text-[11px] text-ink-muted line-clamp-2 leading-relaxed mt-0.5">
                  {recommendation.reason}
                </div>
              </div>
              <div className="w-9 h-9 rounded-full bg-emerald-glow text-bg flex items-center justify-center shrink-0">
                <Play size={16} className="fill-current ml-0.5" />
              </div>
            </button>
          </motion.div>
        </div>
      )}
      {/* 📍 Продолжить слушать */}
      {unfinished.length > 0 && (
        <div className="px-5">
          <div className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold mb-2 px-1">
            📍 Продолжить
          </div>
          <div className="space-y-2">
            {unfinished.map((item) => {
              if (!item.meditation) return null;
              const progress = (item.duration_listened_seconds / item.meditation.duration_seconds) * 100;
              const left = item.meditation.duration_seconds - item.duration_listened_seconds;
              return (
                <button
                  key={item.id}
                  onClick={() => openPlayer(item.meditation!.slug)}
                  className="w-full bg-bg-card border border-line rounded-xl p-3 flex items-center gap-3 active:scale-[0.99] transition-all"
                >
                  <div className="text-2xl shrink-0">{item.meditation.emoji || '🎵'}</div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium truncate">{item.meditation.title}</div>
                    <div className="mt-1.5 h-0.5 bg-line rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-sky-glow" 
                        style={{ width: `${Math.min(100, progress)}%` }} 
                      />
                    </div>
                  </div>
                  <div className="text-[10px] text-ink-dim shrink-0">
                    {formatDuration(left)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 🌬 Дыхательные практики */}
      <BreathingTab />

      {/* 🎙 Практика самоподдержки (13.10.2026): свои слова своим голосом под музыку */}
      <div className="px-5">
        <button
          onClick={() => { haptic('light'); nav('/relax/self'); }}
          className="w-full rounded-2xl p-4 border border-emerald-glow/30 bg-gradient-to-br from-emerald-glow/10 to-sky-glow/5 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
        >
          <div className="w-11 h-11 rounded-xl bg-emerald-glow/15 flex items-center justify-center shrink-0">
            <Mic size={19} className="text-emerald-glow" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm">Практика самоподдержки</div>
            <div className="text-[11px] text-ink-muted leading-snug mt-0.5">
              Запиши слова поддержки своим голосом и слушай под музыку
            </div>
          </div>
          <ChevronRight size={16} className="text-ink-dim shrink-0" />
        </button>
      </div>

      {/* 🎵 Категории медитаций */}
      <div className="px-5">
        <div className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold mb-2 px-1">
          🎵 Медитации
        </div>
        <div className="space-y-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => openCategory(cat.slug)}
              className="w-full bg-bg-card border border-line rounded-xl p-3 flex items-center gap-3 active:scale-[0.99] transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-bg-elevated flex items-center justify-center text-2xl shrink-0">
                {cat.emoji}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-medium">{cat.title}</div>
                {cat.subtitle && (
                  <div className="text-[11px] text-ink-muted truncate mt-0.5">
                    {cat.subtitle}
                  </div>
                )}
              </div>
              <ChevronRight size={16} className="text-ink-dim shrink-0" />
            </button>
          ))}
        </div>
      </div>
      {/* Сноска */}
      <div className="text-[10px] text-ink-dim text-center px-4 pt-2 leading-relaxed">
        💡 Слушай в наушниках — будет глубже погружение
      </div>
    </div>
  );
}
function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
